import { useCallback, useMemo } from 'react';
import { statsApi } from '@/api/stats';
import { useResource } from '@/hooks/useResource';
import { usePolling } from '@/hooks/usePolling';
import { getStatsRefreshInterval, RECENT_ERRORS_LIMIT, RECENT_ERRORS_RANGE } from '@/config/httpStats';
import type {
  DomainHttpStats,
  HttpStatsSummary,
  HttpStatsTimeseries,
  StatsCollector,
  StatsRange,
} from '@/types/stats';

interface StatsFilters {
  range: StatsRange;
  /** Restrict to one node ('' or undefined = all nodes) */
  nodeId?: string;
}

interface HttpStatsOverview {
  summary: HttpStatsSummary;
  collectors: StatsCollector[];
  /** When this data was received */
  updatedAt: Date;
}

/**
 * Statistics page data: the summary for the filters plus the collector state.
 * Refreshes while the tab is visible (every 15 seconds for the short ranges,
 * every minute otherwise); previous data stays on screen while new filters
 * load.
 */
export function useHttpStatsOverview({ range, nodeId }: StatsFilters) {
  const fetcher = useCallback(
    async (signal: AbortSignal): Promise<HttpStatsOverview> => {
      const [summary, collectors] = await Promise.all([
        statsApi.getSummary({ range, node_id: nodeId }, { signal }),
        statsApi.getCollectors({ signal }),
      ]);
      return { summary, collectors: collectors.collectors ?? [], updatedAt: new Date() };
    },
    [range, nodeId]
  );

  const resource = useResource<HttpStatsOverview>(fetcher, { errorMessage: 'Failed to load statistics' });
  usePolling(resource.reload, getStatsRefreshInterval(range), { pauseWhenHidden: true });

  return {
    summary: resource.data?.summary ?? null,
    collectors: resource.data?.collectors ?? [],
    updatedAt: resource.data?.updatedAt ?? null,
    /** End of the complete statistics shown (data_until), null before the first load */
    dataUntil: resource.data?.summary.data_until ?? null,
    isLoading: resource.isLoading,
    isFetching: resource.isFetching,
    error: resource.error,
    reload: resource.reload,
  };
}

/**
 * Requests over time and statuses of one domain (pass null to disable).
 * Refreshes at the page's interval while open.
 */
export function useHttpStatsTimeseries(domain: string | null, { range, nodeId }: StatsFilters) {
  const fetcher = useCallback(
    (signal: AbortSignal) => statsApi.getTimeseries(domain as string, { range, node_id: nodeId }, { signal }),
    [domain, range, nodeId]
  );

  const resource = useResource<HttpStatsTimeseries>(domain ? fetcher : null, {
    errorMessage: 'Failed to load domain statistics',
    keepPreviousData: false,
  });
  usePolling(resource.reload, domain ? getStatsRefreshInterval(range) : null, { pauseWhenHidden: true });

  return {
    timeseries: resource.data ?? null,
    isLoading: resource.isLoading,
    error: resource.error,
  };
}

/**
 * Sites with the most 5xx responses in the last hour (dashboard card).
 * Only sites that actually returned 5xx are listed; a failed load lists
 * nothing (the statistics page reports errors).
 */
export function useRecentServerErrors({ enabled }: { enabled: boolean }): DomainHttpStats[] {
  const fetcher = useCallback(
    (signal: AbortSignal) => statsApi.getSummary({ range: RECENT_ERRORS_RANGE }, { signal }),
    []
  );

  const { data: summary } = useResource<HttpStatsSummary>(enabled ? fetcher : null, {
    errorMessage: 'Failed to load HTTP errors',
  });

  // The summary is already sorted by 5xx desc
  return useMemo(
    () =>
      (summary?.domains ?? [])
        .filter((domain) => domain.status_classes['5xx'] > 0)
        .slice(0, RECENT_ERRORS_LIMIT),
    [summary]
  );
}
